// Sistema RBAC de PCP. Define los 26 módulos canónicos del plan maestro
// (sección H) y el mapeo ruta -> módulo para aplicar permisos por rol.

export type PermissionLevel = { read: boolean; write: boolean; delete: boolean };
export type Permissions = Record<string, PermissionLevel>;

// Mapeo 1:1 entre las rutas del sidebar y los módulos canónicos de permisos.
export const ROUTE_MODULE_MAP: Record<string, string> = {
  '/admin': 'dashboard',
  '/admin/pedidos': 'orders',
  '/admin/produccion': 'produccion',
  '/admin/ventas': 'sales',
  '/admin/clientes': 'customers',
  '/admin/productos': 'products',
  '/admin/tallas': 'sizes',
  '/admin/inventario': 'inventory',
  '/admin/movimientos': 'inventory_movements',
  '/admin/compras': 'purchases',
  '/admin/proveedores': 'suppliers',
  '/admin/distribuidores': 'distributors',
  '/admin/consignaciones': 'consignments',
  '/admin/vendedores': 'salespersons',
  '/admin/comisiones': 'commissions',
  '/admin/pagos': 'payments',
  '/admin/gastos': 'expenses',
  '/admin/donaciones': 'donations',
  '/admin/devoluciones': 'returns',
  '/admin/cambios': 'exchanges',
  '/admin/garantias': 'warranties',
  '/admin/marketing': 'campaigns',
  '/admin/embudo': 'campaigns',
  '/admin/reportes': 'reports',
  '/admin/documentos': 'documents',
  '/admin/usuarios': 'users',
  '/admin/auditoria': 'audit',
  '/admin/reinicio': 'reset',
  '/admin/configuracion': 'settings',
  '/admin/faq': 'faqs',
  '/admin/testimonios': 'settings',
  '/admin/descuentos': 'discounts',
};

// Resuelve el módulo canónico para una ruta dada. Devuelve null si no está mapeada.
export function getModuleForPath(pathname: string): string | null {
  if (pathname === '/admin' || pathname === '/admin/') return 'dashboard';
  return ROUTE_MODULE_MAP[pathname] ?? null;
}

// Permisos efectivos del rol "agente de ventas". Se definen de forma
// explícita (whitelist) porque el agente debe ver SOLO lo suyo y el alcance
// es más restrictivo que la matriz genérica: sin productos, tallas, pagos,
// vendedores, reportes globales ni gastos. `inventory` y `commissions` son de
// solo lectura para él.
export const SALES_AGENT_PERMISSIONS: Permissions = {
  dashboard: { read: true, write: false, delete: false },
  orders: { read: true, write: true, delete: false },
  produccion: { read: true, write: true, delete: false },
  sales: { read: true, write: true, delete: false },
  customers: { read: true, write: true, delete: false },
  commissions: { read: true, write: false, delete: false },
  inventory: { read: true, write: false, delete: false },
  returns: { read: true, write: true, delete: false },
  exchanges: { read: true, write: true, delete: false },
  warranties: { read: true, write: true, delete: false },
};

// Determina si un set de permisos permite leer un módulo.
// El rol `admin` siempre tiene control total (red de seguridad del plan).
// El rol `sales_agent` usa la whitelist explícita definida arriba.
export function hasReadPermission(permissions: Permissions, module: string, role: string): boolean {
  if (role === 'admin') return true;
  if (role === 'sales_agent') return Boolean(SALES_AGENT_PERMISSIONS[module]?.read);
  return Boolean(permissions[module]?.read);
}

// Rol de "Operaciones y Logística". Puede cambiar ESTADOS de pedidos,
// producción y post-venta, pero NO crear registros, NO editar precios/montos/
// abonos y NO borrar. Por eso en la matriz va sin `write` (read-only) y el
// cambio de estado se habilita de forma especial, igual que `sales_agent`.
export const OPERATIONS_ROLE = 'operations';

export const ACCOUNTING_ROLE = 'accountant';

export function isOperationsRole(role: string): boolean {
  return role === OPERATIONS_ROLE;
}

// Módulos donde Operaciones puede cambiar el estado (no crear, no borrar,
// no tocar montos). Se resuelven a nivel de servicio, no de matriz.
export const OPERATIONS_STATUS_MODULES = new Set([
  'orders',
  'returns',
  'exchanges',
  'warranties',
  'distributors',
  'consignments',
]);

// Determina si un rol puede cambiar el estado de un registro del módulo.
// - admin: siempre.
// - operaciones: solo en los módulos de OPERATIONS_STATUS_MODULES.
// - resto de roles: usa el permiso `write` de la matriz.
export function hasStatusPermission(
  permissions: Permissions,
  module: string,
  role: string,
): boolean {
  if (role === 'admin') return true;
  if (isOperationsRole(role)) return OPERATIONS_STATUS_MODULES.has(module);
  return Boolean(permissions[module]?.write);
}